import api from '../api';
import toastService from './toastService';

export interface BackgroundJob {
  id: string;
  type: string;
  projectId: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: {
    current: number;
    total: number;
    percentage: number;
    message?: string;
    currentStep?: string;
  };
  result?: any;
  error?: string;
  metadata?: any;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  updatedAt: string;
}

export interface StartRiskAssessmentJobRequest {
  projectId: number;
  processSteps: Array<{
    name: string;
    description?: string;
    processId?: string;
    stepId?: string | number;
  }>;
  forceRegenerate?: boolean;
  applicableRiskIds?: number[] | null; // 🎯 Filtered risk IDs from scoping questionnaire
}

export interface StartProcessImprovementJobRequest {
  projectId: number;
  forceRegenerate?: boolean;
}

export interface StartRiskAssessmentJobResponse {
  success: boolean;
  message: string;
  jobId: string;
}

class BackgroundJobsAPI {
  // Start a comprehensive risk assessment job
  async startRiskAssessmentJob(request: StartRiskAssessmentJobRequest): Promise<StartRiskAssessmentJobResponse> {
    try {
      const response = await api.post('/api/background-jobs/risk-assessment', request);
      toastService.success('Risk assessment generation started in background');
      return response.data;
    } catch (error: any) {
      console.error('Failed to start risk assessment job:', error);
      const errorMsg = error.response?.data?.details || 'Failed to start background job';
      toastService.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  // Start a process improvement analysis job
  async startProcessImprovementJob(request: StartProcessImprovementJobRequest): Promise<StartRiskAssessmentJobResponse> {
    try {
      const response = await api.post('/api/background-jobs/process-improvement', request);
      toastService.success('Process improvement analysis started in background');
      return response.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.details || 'Failed to start background job';
      toastService.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  // Get job status by ID
  async getJob(jobId: string): Promise<BackgroundJob | null> {
    try {
      const response = await api.get(`/api/background-jobs/${jobId}`);
      if (response.data.success) {
        return response.data.job;
      }
      return null;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to get job status:', error);
      throw new Error(error.response?.data?.details || 'Failed to get job status');
    }
  }

  // Get jobs for current user/project
  async getJobs(filters?: {
    projectId?: number;
    status?: string[];
    type?: string;
    limit?: number;
  }): Promise<BackgroundJob[]> {
    try {
      const params = new URLSearchParams();

      if (filters?.projectId) {
        params.append('projectId', filters.projectId.toString());
      }

      if (filters?.status) {
        params.append('status', filters.status.join(','));
      }

      if (filters?.type) {
        params.append('type', filters.type);
      }

      if (filters?.limit) {
        params.append('limit', filters.limit.toString());
      }

      const response = await api.get(`/api/background-jobs?${params.toString()}`);

      if (response.data.success) {
        return response.data.jobs;
      }

      return [];
    } catch (error: any) {
      console.error('Failed to get jobs:', error);
      throw new Error(error.response?.data?.details || 'Failed to get jobs');
    }
  }

  // Cancel a running job
  async cancelJob(jobId: string): Promise<boolean> {
    try {
      const response = await api.delete(`/api/background-jobs/${jobId}`);
      toastService.info('Background job cancelled successfully');
      return response.data.success;
    } catch (error: any) {
      console.error('Failed to cancel job:', error);
      const errorMsg = error.response?.data?.details || 'Failed to cancel job';
      toastService.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  // Poll job status until completion
  async pollJobStatus(
    jobId: string,
    onProgress?: (job: BackgroundJob) => void,
    pollInterval: number = 1000
  ): Promise<BackgroundJob> {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const job = await this.getJob(jobId);

          if (!job) {
            reject(new Error('Job not found'));
            return;
          }

          // Call progress callback if provided
          if (onProgress) {
            onProgress(job);
          }

          // Check if job is complete
          if (['completed', 'failed', 'cancelled'].includes(job.status)) {
            if (job.status === 'completed') {
              resolve(job);
            } else if (job.status === 'failed') {
              reject(new Error(job.error || 'Job failed'));
            } else {
              reject(new Error('Job was cancelled'));
            }
            return;
          }

          // Continue polling
          setTimeout(poll, pollInterval);
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  }

  // Create a simple promise-based wrapper for easier usage
  async runRiskAssessmentJob(
    request: StartRiskAssessmentJobRequest,
    onProgress?: (job: BackgroundJob) => void
  ): Promise<any> {
    // Start the job
    const startResponse = await this.startRiskAssessmentJob(request);

    // Show loading toast
    const loadingToast = toastService.loading('Generating risk assessments...');

    try {
      // Poll for completion
      const completedJob = await this.pollJobStatus(startResponse.jobId, onProgress);

      // Dismiss loading and show success
      toastService.dismiss(loadingToast);
      toastService.success('Risk assessment generation completed!');

      return completedJob.result;
    } catch (error: any) {
      // Dismiss loading and show error
      toastService.dismiss(loadingToast);
      toastService.error(error.message || 'Risk assessment generation failed');
      throw error;
    }
  }
}

export const backgroundJobsAPI = new BackgroundJobsAPI();
export default backgroundJobsAPI;